import { STORY_SOURCE, story } from '@/content/storyBuilder'

// The fifteenth written page. Everything below comes from the five-part
// full-page capture of digitopia.co/customers/litehouse-foods/ sent on
// 2026-08-28 — "birebir tıpa tıp aynısını tasarla" — with the five
// photographs supplied alongside it.
//
// Its shape is the reference's, part for part: the products photograph in the
// header, then Ben Bryan's AIMI-framework quote standing alone on the white
// page, then the grey bands. "Introduction" is a `band` section rather than
// `intro` for one reason — `intro` is drawn before the sections, and on the
// reference the quote comes first. The two render identically (`PANEL`,
// centred), so this costs the page nothing and keeps the order.
//
// Then the reference's own alternation, which the template already has both
// halves of: the Challenge and Key Outcomes are Intel AI's shape
// (`photoSide: 'right'` with `photoInPanel`, the panel enclosing both
// columns) and the Solution between them is its mirror (`photoSide: 'left'`),
// which is what the capture shows — three full-width panels, the picture
// falling on alternate sides.
//
// The closing card is the testimonial template the request asked for:
// `content/testimonials/litehouse-foods.ts` holds Ben Bryan's second, longer
// quote — the one the reference closes on, which is *not* the one at the top
// of the page — and `StoryBody` draws it. The mark is the 300×300 transparent
// `litehousecard.png` already in the tree, which is better than the 150×100
// white-ground file offered with the request; the portrait is new.
export const added = story('litehouse-foods', 'Litehouse Foods', {
  doc: '4e3be04e-7f33-4400-a721-7c1882f3cc6c-Litehouse Foods and Digitopia.pdf',
  lede: [
    'Litehouse Foods, a 100% employee-owned leader in food manufacturing, transformed its AI '
    + 'journey from scattered experiments into an evidence-based, company-wide roadmap with '
    + 'Digitopia and Fisher Management Partners.',
  ],
  heroPhoto: {
    src: '/photo/litehouse-product-range.webp',
    alt: 'Litehouse Foods’ range on a wooden table: dressings, dips, blue cheese and herbs '
      + 'in jars, tubs and bottles, with apples, radishes and a yellow pepper around them.',
  },
  sections: [
    {
      centred: true,
      body: [
        '“The AIMI framework has been especially valuable in helping us think '
        + 'holistically, across data, technology, operations, governance, and culture, so that '
        + 'AI becomes part of how we run the business, not just a set of isolated initiatives. '
        + 'Most importantly, this effort has positioned us to move forward with confidence. We '
        + 'now have a shared understanding of where we are, where we need to go, and how to '
        + 'make measurable progress on our AI journey.”',
        'Ben Bryan, Manager, Applications, Litehouse Foods',
      ],
    },
    {
      heading: 'Introduction',
      band: true,
      centred: true,
      body: [
        'Litehouse Foods has grown from humble beginnings into a leading employee-owned food '
        + 'company, bringing together a diverse portfolio of trusted brands and innovative '
        + 'products enjoyed by consumers nationwide. Guided by a shared purpose and a '
        + 'commitment to Heart, Ownership, Excellence, and Integrity, Litehouse Foods '
        + 'continues to evolve to meet the needs of customers, consumers, and communities. As '
        + 'the organization looks to the future, AI is emerging as a critical enabler of that '
        + 'growth, helping Employee Owners work smarter, accelerate innovation, and strengthen '
        + 'the company’s ability to deliver exceptional food experiences. Recognizing the '
        + 'transformative potential of AI, Litehouse Foods has embraced it not as a peripheral '
        + 'initiative, but as a strategic capability that supports its vision for the future.',
      ],
    },
    {
      heading: 'How Did it Start?',
      band: true,
      centred: true,
      body: [
        'By partnering with Digitopia and Fisher Management Partners, Litehouse Foods sought '
        + 'to move beyond the experimental phase of AI and establish a sophisticated, '
        + 'evidence-based approach to organizational maturity.',
        'As Ben Bryan, Manager, Applications at Litehouse Foods, put it: “At Litehouse Foods, '
        + 'we’ve always been interested in AI, but like many organizations, we were navigating '
        + 'a mix of curiosity, optimism, and uncertainty about how to move forward.”',
      ],
    },
    {
      heading: 'The Challenge: Navigating the AI Hype',
      band: true,
      photoSide: 'right',
      photoInPanel: true,
      photo: {
        src: '/photo/litehouse-packing-line.webp',
        alt: 'A Litehouse Foods packing line: an Employee Owner in a white coat, hairnet, '
          + 'safety glasses and blue hard hat carrying a stack of cartons past the conveyor.',
      },
      body: [
        'Litehouse Foods recognized the immense potential of Artificial Intelligence. However, '
        + 'they faced the challenge of cutting through market hype to identify where AI could '
        + 'deliver genuine, sustainable business value. They needed a way to align leadership, '
        + 'measure their current state, and build a roadmap based on evidence rather than '
        + 'assumptions.',
      ],
    },
    {
      heading: 'The Solution: A Collaborative AI Readiness Journey',
      band: true,
      photoSide: 'left',
      photo: {
        src: '/photo/aimi-assessment-presentation.webp',
        alt: 'A workshop in a production hall: a facilitator presenting an AIMI score and '
          + 'benchmark on the Impact Platform to colleagues around the table.',
      },
      body: [
        'Litehouse Foods engaged Digitopia and Fisher to undergo a comprehensive AI Maturity '
        + 'Index (AIMI) assessment. This partnership combined Fisher’s pragmatic AI strategy '
        + 'development expertise with Digitopia’s Impact Platform to shift the conversation '
        + 'from “what AI can do” to “what Litehouse Foods should do” to achieve its business '
        + 'goals.',
        'Through a series of focused, collaborative workshops, the teams deep-dived into the '
        + 'business to co-create a practical, enterprise-wide strategy.',
      ],
    },
    {
      heading: 'Key Outcomes:',
      band: true,
      photoSide: 'right',
      photoInPanel: true,
      photo: {
        src: '/photo/boardroom-impact-platform-applause.webp',
        alt: 'A meeting room applauding a score presented on the Impact Platform, the '
          + 'benchmark chart on the screen behind the table.',
      },
      items: [
        {
          lead: 'Evidence-Based Readiness Baseline',
          text: 'Replaced subjective opinions with a definitive, evidence-based score, '
            + 'providing a “North Star” for all future AI investments.',
        },
        {
          lead: 'Prioritized ROI Roadmap',
          text: 'Identified high-impact use cases that move beyond isolated pilots into '
            + 'sustained, value-generating execution.',
        },
        {
          lead: 'C-Suite Alignment',
          text: 'Bridged the gap between technical potential and business leadership, creating '
            + 'a shared language for AI across the executive team.',
        },
        {
          lead: 'Operational Confidence',
          text: 'Equipped the organization with the clarity needed to scale AI safely, '
            + 'ensuring the infrastructure and culture are ready to absorb rapid innovation.',
        },
      ],
    },
    {
      heading: 'Looking Ahead',
      band: true,
      centred: true,
      body: [
        'By completing the AIMI journey, Litehouse Foods hasn’t just adopted a new set of '
        + 'tools; they have evolved their operating model. They now stand as a benchmark for '
        + 'AI excellence in the food manufacturing sector, equipped with the data-driven '
        + 'clarity to turn AI potential into operational reality.',
      ],
    },
  ],
  check:
    'This page’s words were transcribed from a five-part full-page capture of '
    + `${STORY_SOURCE}/litehouse-foods/ supplied on 2026-08-28, not from a file we hold, so `
    + 'please proofread it against the source before production — in particular the two Ben '
    + 'Bryan quotes, which are different statements and are easy to conflate: the AIMI-'
    + 'framework one stands alone under the header, and the longer one is the closing card. '
    + 'The reference sets American spellings (“organization”, “Prioritized”, “recognized”) '
    + 'and they are left exactly as it sets them, against this site’s British copy elsewhere, '
    + 'because the request was for the same page. Three differences of shape, each a '
    + 'template-wide decision rather than this page’s: the header eyebrow reads “Digitopia / '
    + 'Customer Success Story” where digitopia.co reads “SUCCESS STORIES”; the headline is '
    + 'the company name where the reference runs “From AI Curiosity to Strategic Maturity: '
    + 'Litehouse Foods”; and the reference’s five “Read More” / “Download the Case Study” '
    + 'buttons are here the one “Read the full story (PDF)” button in the header, pointing at '
    + 'the same document. One production note: the four photographs are the small originals '
    + 'supplied with the request — 768×512, 400×225 and two at 1800×1200 — so the packing-'
    + 'line picture in “The Challenge” is drawn at 400px wide and will look soft on a large '
    + 'screen until a larger file arrives. And one to look at on the closing card: Ben Bryan’s '
    + 'portrait was replaced on 2026-08-28 with the 900×900 black-and-white file supplied with '
    + 'the request, so it now matches the other portraits in size and treatment; what is still '
    + 'worth checking on the page is whether its ground is transparent like theirs or prints '
    + 'as a white square on the grey band.',
})

/** Its place in the hand-placed order the imported Customers page set. */
export const order = 0
